import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import DigitsContainer from '../../../generic-components/DigitsContainer';
import BlackDotContainer from './BlackDotContainer';
import { useStore } from '../../../store';
import { ACTIVATE_STYLES_TEXTS, ACTIVATION_FLOWS } from '../constants';
import { IPayload, useActivateCardQuery } from '../../../hooks/query/useActivateCardQuery';
import { MIN_YEAR_TO_ACTIVATE_CARD, MONTHS } from '../../../constants/general';
import { getMostRelevantCard } from '../../../helpers/cardStatus.helper';

interface IProps {
  goToStep: (step: number) => void;
}

const ActivateWithExpDate = ({ goToStep }: IProps) => {
  const { t } = useTranslation();
  const { customerId } = useStore(state => state.currUser());
  const { getPersonalAccount } = useStore(state => state);
  const cardData = getMostRelevantCard(getPersonalAccount().cards);
  const { EXPIRATION_DATE } = ACTIVATION_FLOWS;

  const [data, setData] = useState({
    lastFourCard: '', month: '', year: '',
  });
  const [isError, setIsError] = useState<boolean>(false);

  const getPayload = () => {
    const res:IPayload = { last4Card: '' };
    res.last4Card = data.lastFourCard;
    res.expirationDate = `${data.year}-${data.month}`;
    return res;
  };

  const disabledContinueButton = (!(data.lastFourCard.length === 4 && data.month.length === 2 && parseInt(data.month, 10) <= MONTHS.length && data.year.length === 2 && parseInt(data.year, 10) >= MIN_YEAR_TO_ACTIVATE_CARD));

  const { refetch: activateCard, isLoading: isActivationInProcess } = useActivateCardQuery({
    customerId,
    activationType: EXPIRATION_DATE,
    cardId: cardData.cardId,
    payload: getPayload(),
    onSuccess: async () => {
      goToStep(2);
    },
    onFailure: () => { setIsError(true); },
  });

  const handleFulfillCardLast4 = (last4: string) => {
    setData({ ...data, lastFourCard: last4 });
  };

  const handleFulfillMonth = (month:string) => {
    setData({ ...data, month });
  };

  const handleFulfillYear = (year:string) => {
    setData({ ...data, year });
  };

  return (
    <div className={ACTIVATE_STYLES_TEXTS.CONTAINER}>
      <span className={ACTIVATE_STYLES_TEXTS.HEADER_STYLE}>{t(ACTIVATE_STYLES_TEXTS.HEADER)}</span>
      <span className={ACTIVATE_STYLES_TEXTS.TEXT_STYLE}>{t(ACTIVATE_STYLES_TEXTS.TEXT)}</span>
      <span className={ACTIVATE_STYLES_TEXTS.PLACEHOLDER_STYLE}>{t(ACTIVATE_STYLES_TEXTS.PLACEHOLDER_EXP_DATE)}</span>
      <div className={ACTIVATE_STYLES_TEXTS.DIGITS_CONTAINER_EXP_DATE_WRAPPER}>
        <DigitsContainer needCleanParentState resetIsError={() => { if (isError) setIsError(false); }} containerClassName={ACTIVATE_STYLES_TEXTS.DIGITS_CONTAINER} isError={false} isLoading={false} numOfDigits={2} onFulfill={handleFulfillMonth} />
        <span className={ACTIVATE_STYLES_TEXTS.SLASH_STYLE}>{ACTIVATE_STYLES_TEXTS.SLASH}</span>
        <DigitsContainer needCleanParentState resetIsError={() => { if (isError) setIsError(false); }} containerClassName={ACTIVATE_STYLES_TEXTS.DIGITS_CONTAINER} isError={false} isLoading={false} numOfDigits={2} onFulfill={handleFulfillYear} />
      </div>
      <span className={ACTIVATE_STYLES_TEXTS.PLACEHOLDER_STYLE}>{t(ACTIVATE_STYLES_TEXTS.PLACEHOLDER_LAST_4)}</span>
      <div className={ACTIVATE_STYLES_TEXTS.DIGITS_CONTAINER_LAST_4_WRAPPER}>
        <BlackDotContainer containerClassName={ACTIVATE_STYLES_TEXTS.BLACK_DOT_CONTAINER} dotsAmount={12} dotsContainerClassName='' dotsInGroup={4} />
        <DigitsContainer needCleanParentState resetIsError={() => { setIsError(false); }} containerClassName={ACTIVATE_STYLES_TEXTS.DIGITS_CONTAINER} isError={false} isLoading={false} numOfDigits={4} onFulfill={handleFulfillCardLast4} />
      </div>
      <div className={ACTIVATE_STYLES_TEXTS.BUTTONS_ERRORS_CONTAINER_EXP_DATE}>
        <div className={ACTIVATE_STYLES_TEXTS.ERRORS_CONTAINER_STYLE}>{isError ? t(ACTIVATE_STYLES_TEXTS.ERROR_TEXT_EXP_DATE) : ''}</div>
        <LiliButton type={LiliButtonType.BACK} onClick={() => { goToStep(0); }} title={t(ACTIVATE_STYLES_TEXTS.BACK_BUTTON)} className={ACTIVATE_STYLES_TEXTS.BACK_BUTTON_STYLE} id='cancelActivationButton' />
        <LiliButton
          onClick={() => { activateCard(); }}
          className={ACTIVATE_STYLES_TEXTS.ACTIVATION_BUTTON_STYLE}
          id='continueActivationButton'
          disabled={disabledContinueButton || isActivationInProcess}
          isLoading={isActivationInProcess}
          title={t(ACTIVATE_STYLES_TEXTS.ACTIVATION_BUTTON_TEXT)}
        />
      </div>
    </div>
  );
};

export default ActivateWithExpDate;
